import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import type { Item } from './ItemForm';
import { whereShort } from './whereNow';

/** How long the pointer (or the keyboard focus) must rest on the name before the information shows. */
export const INFO_DELAY_MS = 500;

/**
 * What the information on the name shows (remark 4/29): the description and where the item is (its default location
 * and the short "Gdzie teraz"). Only fields the role may see (`visible`, rule 7a); empty ones are left out.
 */
export function infoLines(item: Item, visible: (field: string) => boolean, names: ReadonlyMap<string, string>): [string, string][] {
  const out: [string, string][] = [];
  if (visible('notes') && item.notes?.trim()) out.push(['Opis', item.notes.trim()]);
  const home = visible('defaultLocationId') && item.defaultLocationId ? names.get(item.defaultLocationId) : undefined;
  const now = visible('placements') ? whereShort(item.placements ?? [], names) : '';
  const where = [home ? `${home} (domyślna)` : '', now ? `teraz: ${now}` : ''].filter(Boolean).join('; ');
  if (where) out.push(['Lokalizacja', where]);
  return out;
}

/**
 * The item's name with its information: shown after a moment of hovering or keyboard focus, hidden on leaving,
 * blur or Escape; on a phone (no hover) the small "i" next to the name opens and closes it. The name button gets the
 * information as its description (`aria-describedby`).
 */
export function ItemInfo({
  item,
  visible,
  names,
  children,
}: {
  item: Item;
  visible: (field: string) => boolean;
  names: ReadonlyMap<string, string>;
  children: (describedBy: string | undefined) => ReactNode;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lines = infoLines(item, visible, names);
  const clear = () => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(() => clear, []);
  if (lines.length === 0) return <>{children(undefined)}</>;

  const later = () => {
    clear();
    timer.current = setTimeout(() => setOpen(true), INFO_DELAY_MS);
  };
  const hide = () => {
    clear();
    setOpen(false);
  };
  const shown = open || pinned;
  return (
    <span
      className="cat-info"
      onMouseEnter={later}
      onMouseLeave={() => !pinned && hide()}
      onFocus={later}
      onBlur={(e) => {
        if (!(e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget))) {
          hide();
          setPinned(false);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && shown) {
          e.stopPropagation();
          hide();
          setPinned(false);
        }
      }}
    >
      {children(id)}
      <button
        type="button"
        className="cat-info-btn"
        aria-label={`Informacje: ${item.name}`}
        aria-expanded={pinned}
        onClick={() => {
          clear();
          setOpen(false);
          setPinned(!pinned);
        }}
      >
        i
      </button>
      {/* always in the DOM (hidden), so the name button keeps its description for screen readers */}
      <span id={id} role="tooltip" className="cat-info-pop" hidden={!shown}>
        {lines.map(([label, value]) => (
          <span key={label} className="cat-info-line">
            <span className="cat-info-label">{label}:</span> {value}
          </span>
        ))}
      </span>
    </span>
  );
}
